import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faCloudArrowUp,
  faFileExcel,
  faFileLines,
  faPaperPlane,
  faRotateLeft,
  faTable,
} from '@fortawesome/free-solid-svg-icons';
import MessageBubble from './MessageBubble';
import TypingIndicator from './TypingIndicator';
import InlineNotice from './InlineNotice';
import {
  getRelationMessages,
  sendRelationMessage,
  updateRelationChat,
  uploadRelationFiles,
} from '../services/api';
import {
  createMessageId,
  normalizeBackendResponse,
  sanitizeMessages,
} from '../utils/messageNormalizer';

const TYPE_CARDS = [
  {
    id: 'csv',
    label: 'CSV',
    description: 'Upload two comma-separated files for join analysis.',
    icon: faFileLines,
  },
  {
    id: 'excel',
    label: 'Excel',
    description: 'Upload two Excel workbooks to detect shared columns.',
    icon: faFileExcel,
  },
];

function normalizeAllowedFile(type, file) {
  if (!file) return true;
  const name = file.name.toLowerCase();
  if (type === 'csv') return name.endsWith('.csv');
  return name.endsWith('.xlsx') || name.endsWith('.xls');
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 && unit > 0 ? 1 : 0)} ${units[unit]}`;
}

function sortRelationMessages(messages) {
  return [...messages].sort((a, b) => {
    const aTime = new Date(a.created_at || 0).getTime();
    const bTime = new Date(b.created_at || 0).getTime();
    if (aTime !== bTime) return aTime - bTime;
    if (a.role !== b.role) return a.role === 'user' ? -1 : 1;
    return 0;
  });
}

export default function RelationPanel({
  relationSession,
  relationChat,
  onChatRenamed,
  onCreateSession,
  onCreateChat,
  onSelectSession,
  onSelectChat,
}) {
  const [selectedType, setSelectedType] = useState(null);
  const [files, setFiles] = useState([null, null]);
  const [relationMessages, setRelationMessages] = useState([]);
  const [input, setInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!relationChat?.id) {
      setRelationMessages([]);
      setInput('');
      setSending(false);
      return;
    }

    let alive = true;
    setRelationMessages([]);
    setSending(false);
    getRelationMessages(relationChat.id)
      .then(data => {
        if (!alive) return;
        const ordered = sortRelationMessages(sanitizeMessages(data));
        setRelationMessages(prev => (JSON.stringify(prev) === JSON.stringify(ordered) ? prev : ordered));
      })
      .catch(() => toast.error('Could not load relation messages'));

    return () => {
      alive = false;
    };
  }, [relationChat?.id]);

  useEffect(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, [relationMessages, relationChat?.id]);

  useEffect(() => {
    if (relationSession) {
      setSelectedType(null);
      setFiles([null, null]);
      setDragIndex(null);
      setUploading(false);
    }
  }, [relationSession?.id]);

  const resetRelation = () => {
    onSelectSession(null);
    onSelectChat(null);
    setSelectedType(null);
    setFiles([null, null]);
    setRelationMessages([]);
    setInput('');
    setUploading(false);
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setFiles([null, null]);
  };

  const setFileAt = (index, file) => {
    if (!selectedType) {
      toast.error('Pick CSV or Excel first');
      return;
    }
    if (file && !normalizeAllowedFile(selectedType, file)) {
      toast.error(selectedType === 'csv' ? 'Only .csv files are allowed' : 'Only .xlsx or .xls files are allowed');
      return;
    }
    setFiles(prev => {
      const next = [...prev];
      next[index] = file;
      return next;
    });
  };

  const handleDrop = (index, e) => {
    e.preventDefault();
    setDragIndex(null);
    const file = e.dataTransfer.files?.[0];
    if (file) setFileAt(index, file);
  };

  const startAnalysis = async () => {
    if (uploading) return;
    if (!selectedType) return toast.error('Choose CSV or Excel first');
    if (!files[0] || !files[1]) return toast.error('Please upload exactly 2 files');
    if (!normalizeAllowedFile(selectedType, files[0]) || !normalizeAllowedFile(selectedType, files[1])) {
      return toast.error('Both files must match the selected type');
    }

    setUploading(true);
    try {
      const result = await uploadRelationFiles(files, selectedType);
      await onCreateSession(result);
      if (result.file_is_unreliable) {
        toast('This relation upload has inconsistent formatting. Results may be inaccurate.', {
          icon: '⚠️',
          duration: 6000,
        });
      }
      toast.success('Relation session ready');
    } catch (err) {
      // On a client-side timeout the backend may still be processing the
      // files — don't imply an immediate retry is safe, since that would
      // fire a second upload at the same filenames while the first is
      // still finishing on the server.
      if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
        toast.error(
          'This is taking longer than expected. The files may still be processing — please wait a moment before trying again.',
          { duration: 7000 },
        );
      } else {
        toast.error(err.message || 'Failed to analyze files');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending || !relationChat) return;
    const shouldAutoRename = relationMessages.length === 0 && relationChat.title === 'New Chat';
    const tempUser = {
      id: createMessageId('user'),
      relation_chat_id: relationChat.id,
      role: 'user',
      content: text,
      created_at: new Date().toISOString(),
    };
    setInput('');
    setRelationMessages(prev => [...prev, tempUser]);
    setSending(true);

    try {
      const result = await sendRelationMessage({
        relation_chat_id: relationChat.id,
        content: text,
      });
      setRelationMessages(prev => {
        const filtered = prev.filter(m => m.id !== tempUser.id);
        return sortRelationMessages(sanitizeMessages([...filtered, ...normalizeBackendResponse(result)]));
      });

      if (shouldAutoRename) {
        const newTitle = text.slice(0, 48);
        updateRelationChat(relationChat.id, { title: newTitle })
          .then(updated => {
            onChatRenamed && onChatRenamed(updated);
          })
          .catch(() => {});
      }
    } catch (err) {
      setRelationMessages(prev => prev.filter(m => m.id !== tempUser.id));
      toast.error(err.message || 'Failed to send relation question');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handlePaginate = async (query, page) => {
    if (!relationChat || sending || !query) return;
    setSending(true);
    try {
      const result = await sendRelationMessage({
        relation_chat_id: relationChat.id,
        content: `Show page ${page + 1}`,
        page,
        query,
      });
      setRelationMessages(prev => sortRelationMessages(sanitizeMessages([...prev, ...normalizeBackendResponse(result)])));
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (err) {
      toast.error(err.message || 'Failed to load relation page');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggest = (question) => {
    setInput(question);
    inputRef.current?.focus();
  };

  const renderUploadBox = (index) => {
    const file = files[index];
    const title = index === 0 ? 'File 1' : 'File 2';
    return (
      <div
        style={{
          ...s.uploadBox,
          ...(dragIndex === index ? s.uploadBoxActive : {}),
        }}
        onDragOver={e => { e.preventDefault(); setDragIndex(index); }}
        onDragLeave={() => setDragIndex(null)}
        onDrop={e => handleDrop(index, e)}
      >
        <div style={s.uploadTop}>
          <div style={s.uploadBadge}>{title}</div>
          <label style={s.pickBtn}>
            Browse
            <input
              type="file"
              accept={selectedType === 'csv' ? '.csv' : '.xlsx,.xls'}
              style={{ display: 'none' }}
              onChange={e => setFileAt(index, e.target.files?.[0] || null)}
            />
          </label>
        </div>
        {file ? (
          <div style={s.fileCard}>
            <FontAwesomeIcon icon={selectedType === 'csv' ? faFileLines : faFileExcel} aria-hidden="true" />
            <div style={s.fileMeta}>
              <span style={s.fileName}>{file.name}</span>
              <span style={s.fileSize}>{formatBytes(file.size)}</span>
            </div>
          </div>
        ) : (
          <div style={s.uploadEmpty}>
            <FontAwesomeIcon icon={faCloudArrowUp} style={{ fontSize: 24 }} aria-hidden="true" />
            <span>Drag and drop or browse a {selectedType || 'selected'} file</span>
          </div>
        )}
      </div>
    );
  };

  if (!relationSession) {
    return (
      <div style={s.panel}>
        <div style={s.header}>
          <div>
            <div style={s.kicker}>Relation Connect</div>
            <h2 style={s.title}>Connect two related files</h2>
          </div>
          {selectedType && (
            <button onClick={resetRelation} style={s.resetBtn}>
              <FontAwesomeIcon icon={faRotateLeft} aria-hidden="true" /> Reset
            </button>
          )}
        </div>

        {!selectedType ? (
          <div style={s.typeGrid}>
            {TYPE_CARDS.map(card => (
              <button key={card.id} onClick={() => handleTypeSelect(card.id)} style={s.typeCard}>
                <div style={s.typeIcon}><FontAwesomeIcon icon={card.icon} aria-hidden="true" /></div>
                <div style={s.typeLabel}>{card.label}</div>
                <div style={s.typeDesc}>{card.description}</div>
              </button>
            ))}
          </div>
        ) : (
          <div style={s.uploadStage}>
            <div style={s.stageTop}>
              <div style={s.selectedChip}>{selectedType.toUpperCase()}</div>
              <div style={s.stageNote}>Upload exactly two files with matching headers</div>
            </div>
            <div style={s.uploadGrid}>
              {renderUploadBox(0)}
              {renderUploadBox(1)}
            </div>
            <button
              onClick={startAnalysis}
              disabled={uploading || !files[0] || !files[1]}
              style={{ ...s.startBtn, ...((uploading || !files[0] || !files[1]) ? s.startBtnDisabled : {}) }}
            >
              {uploading ? 'Analyzing...' : <>Start Analysis <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" /></>}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!relationChat) {
    return (
      <div style={s.panel}>
        <div style={s.header}>
          <div>
            <div style={s.kicker}>Relation Chat</div>
            <h2 style={s.title}>{relationSession.name}</h2>
          </div>
          <button onClick={resetRelation} style={s.resetBtn}>
            <FontAwesomeIcon icon={faRotateLeft} aria-hidden="true" /> New Relation
          </button>
        </div>
        <div style={s.welcome}>
          <div style={s.welcomeIcon}><FontAwesomeIcon icon={faTable} aria-hidden="true" /></div>
          <h3 style={s.welcomeTitle}>Select or create a chat</h3>
          <p style={s.welcomeSub}>Use the relation section in the sidebar to open an existing chat or start a new one for this session.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={s.panel}>
      <div style={s.header}>
        <div>
          <div style={s.kicker}>Relation Chat</div>
          <h2 style={s.title}>{relationSession.name}</h2>
        </div>
        <button onClick={resetRelation} style={s.resetBtn}>
          <FontAwesomeIcon icon={faRotateLeft} aria-hidden="true" /> New Relation
        </button>
      </div>

      {relationSession?.file_is_unreliable && (
        <div style={s.warningBanner}>
          <InlineNotice variant="warning" text="This file has inconsistent formatting (some rows have different column counts). Results may be inaccurate. Please clean the file for better accuracy." />
        </div>
      )}

      <div style={s.messages}>
        {relationMessages.length === 0 ? (
          <div style={s.welcome}>
            <div style={s.welcomeIcon}><FontAwesomeIcon icon={faTable} aria-hidden="true" /></div>
            <h3 style={s.welcomeTitle}>Relation analysis is ready</h3>
            <p style={s.welcomeSub}>The two files share join key <strong>{relationSession.join_key}</strong>. Ask for comparisons, filters, summaries, or joins.</p>
          </div>
        ) : null}

        {relationMessages.filter(Boolean).map((msg, i) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            isLast={i === relationMessages.filter(Boolean).length - 1}
            onPaginate={handlePaginate}
            onSuggest={handleSuggest}
          />
        ))}

        {sending && <TypingIndicator />}
        <div ref={bottomRef} />
      </div>

      <div style={s.inputWrap}>
        <div style={s.inputBox}>
          <textarea
            ref={inputRef}
            style={s.textarea}
            placeholder="Ask about the relationship between the two files..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            rows={1}
            disabled={sending}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || sending}
            style={{ ...s.sendBtn, ...((!input.trim() || sending) ? s.sendBtnDisabled : {}) }}
          >
            <FontAwesomeIcon icon={faPaperPlane} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

const s = {
  panel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    background: 'var(--bg-primary)',
    overflow: 'hidden',
  },
  header: {
    padding: '14px 24px',
    background: 'var(--bg-primary)',
    borderBottom: '1px solid var(--border-subtle)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    flexShrink: 0,
  },
  kicker: { fontSize: 10, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text-muted)' },
  title: { fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', marginTop: 4 },
  resetBtn: {
    padding: '8px 12px',
    borderRadius: 10,
    border: '1px solid var(--border)',
    background: 'var(--bg-tertiary)',
    color: 'var(--text-primary)',
    fontSize: 12,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  typeGrid: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 18,
    padding: 24,
    alignContent: 'center',
  },
  typeCard: {
    minHeight: 190,
    borderRadius: 22,
    border: '1px solid var(--border-subtle)',
    background: 'var(--bg-secondary)',
    padding: 20,
    textAlign: 'left',
    cursor: 'pointer',
    color: 'var(--text-primary)',
  },
  typeIcon: { width: 42, height: 42, borderRadius: 14, background: 'var(--accent-subtle)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, marginBottom: 16 },
  typeLabel: { fontSize: 22, fontWeight: 800, marginBottom: 10 },
  typeDesc: { fontSize: 13, lineHeight: 1.6, color: 'var(--text-muted)' },
  uploadStage: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: 24,
    gap: 18,
  },
  stageTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  selectedChip: { padding: '6px 10px', borderRadius: 999, background: 'var(--accent-subtle)', color: 'var(--accent)', fontSize: 11, fontWeight: 800, letterSpacing: '0.6px' },
  stageNote: { color: 'var(--text-muted)', fontSize: 13 },
  uploadGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16, flex: 1, alignItems: 'stretch' },
  uploadBox: {
    borderRadius: 20,
    border: '1.5px dashed var(--border-subtle)',
    background: 'var(--bg-secondary)',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: 260,
  },
  uploadBoxActive: { borderColor: 'var(--accent)', background: 'var(--accent-subtle)' },
  uploadTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  uploadBadge: { fontSize: 12, fontWeight: 800, color: 'var(--text-primary)' },
  pickBtn: { fontSize: 12, fontWeight: 700, color: 'var(--accent)', cursor: 'pointer' },
  uploadEmpty: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    color: 'var(--text-muted)',
    textAlign: 'center',
    padding: 18,
    lineHeight: 1.5,
  },
  fileCard: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    borderRadius: 14,
    background: 'var(--bg-primary)',
    border: '1px solid var(--border-subtle)',
    marginTop: 18,
  },
  fileMeta: { display: 'flex', flexDirection: 'column', gap: 4, overflow: 'hidden' },
  fileName: { fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileSize: { fontSize: 11, color: 'var(--text-muted)' },
  startBtn: {
    alignSelf: 'flex-end',
    padding: '12px 18px',
    borderRadius: 14,
    border: 'none',
    background: 'var(--accent)',
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  startBtnDisabled: { opacity: 0.45, cursor: 'not-allowed' },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
  },
  warningBanner: { margin: '14px 24px 0' },
  welcome: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
  },
  welcomeIcon: { fontSize: 44, color: 'var(--accent)', marginBottom: 14 },
  welcomeTitle: { fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 },
  welcomeSub: { fontSize: 14, color: 'var(--text-muted)', maxWidth: 460, lineHeight: 1.6 },
  inputWrap: {
    padding: '16px 24px 20px',
    background: 'var(--bg-primary)',
    flexShrink: 0,
  },
  inputBox: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-end',
    background: 'var(--bg-secondary)',
    borderRadius: 16,
    border: '1px solid var(--border-subtle)',
    padding: '10px 12px',
  },
  textarea: {
    flex: 1,
    background: 'none',
    border: 'none',
    outline: 'none',
    resize: 'none',
    fontSize: 14,
    color: 'var(--text-primary)',
    lineHeight: 1.6,
    fontFamily: 'var(--font-main)',
    maxHeight: 140,
    overflowY: 'auto',
  },
  sendBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sendBtnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
};
